import { Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, switchMap } from 'rxjs';

import { Prices } from 'src/app/api/owcc/models';
import { fromCarl } from 'src/app/store/common/common.selectors';

/**
 * FormattedPricePipe is an Angular pipe that formats and displays price information
 * from a Prices object. It can conditionally show either the net price or the gross price,
 * and it allows for a custom text replacement for zero values. This pipe is standalone,
 * meaning it can be used without being declared in an Angular module.
 *
 * Usage:
 * This pipe can be used in Angular templates to display formatted price information.
 * It supports showing either the net or gross price based on application configuration
 * or an explicit parameter. Additionally, it can replace zero prices with custom text.
 *
 * Example:
 * {{ product.prices | formattedPrice:'Free' }}
 * This will display the product's price, or "Free" if the price is zero.
 *
 * Parameters:
 * - obj: Prices - The Prices object containing the price information to be formatted.
 * - replaceZeroWithText: string (optional) - Text to display instead of a zero price.
 * - showNetPrice: boolean | null (optional) - Explicitly sets whether to show the net price
 *   (true), gross price (false), or use application configuration (null).
 *
 * Returns:
 * Observable<string> - An observable that emits the formatted price string.
 *
 * Details:
 * - The pipe subscribes to the application's configuration to determine whether to show
 *   net or gross prices by default, unless explicitly overridden by the showNetPrice parameter.
 * - It uses the Prices object to determine the correct price to display, formatting it
 *   according to the specified conditions.
 * - If the price is zero and replaceZeroWithText is provided, it displays the replacement text.
 * - This pipe is marked as standalone, allowing it to be used independently of an Angular module.
 */
@Pipe({
  name: 'formattedPrice',
  standalone: true
})
export class FormattedPricePipe implements PipeTransform {
  public constructor(private readonly store: Store) {}

  public transform(obj: Prices, replaceZeroWithText?: string, showNetPrice = null): Observable<string> {
    if (obj === null) {
      return of('');
    }

    const getPrice = (showNet: boolean | null, priceObj: Prices, replaceText: string | undefined): string => {
      const price = showNet ? priceObj?.formattedNetPrice : priceObj?.formattedPrice;
      return priceObj?.price === 0 && !!replaceText ? replaceText : price || '';
    };

    return showNetPrice === null
      ? this.store.select(fromCarl.selectConfig('financial.showNetPrice')).pipe(
          switchMap(showNetPriceConfig => {
            const transShowNetPrice = !!(showNetPriceConfig || showNetPrice);
            return of(getPrice(transShowNetPrice, obj, replaceZeroWithText));
          })
        )
      : of(getPrice(null, obj, replaceZeroWithText));
  }
}
